import React from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  LayoutDashboard,
  BookOpen,
  Target,
  BarChart3,
  MoreHorizontal,
  Play,
} from 'lucide-react';
import './StudentPortalShell.css';

const MOBILE_NAV = [
  { id: 'dashboard', label: 'Home', icon: LayoutDashboard },
  { id: 'courses', label: 'Courses', icon: BookOpen },
  { id: 'learningPath', label: 'Path', icon: Target },
  { id: 'progress', label: 'Progress', icon: BarChart3 },
];

const StudentPortalShell = ({
  studentInfo,
  stats,
  activeTab,
  activeTabMeta,
  navGroups,
  quickTabs = [],
  onTabChange,
  searchTerm,
  onSearchChange,
  showContinueFab,
  onContinueFocus,
  sidebarCollapsed,
  onToggleCollapse,
  sidebarOpen,
  onToggleSidebar,
  isMobile,
  onLogout,
  onOpenSettings,
  onRefresh,
  error,
  children,
}) => {
  const activeLabel = activeTabMeta?.title || 'Student Portal';

  return (
    <div className="sp-shell">
      <div className="sp-ambient" aria-hidden>
        <div className="sp-ambient__orb sp-ambient__orb--violet" />
        <div className="sp-ambient__orb sp-ambient__orb--cyan" />
        <div className="sp-ambient__orb sp-ambient__orb--amber" />
      </div>

      {isMobile && (
        <div className="sp-mobile-search">
          <Search size={16} aria-hidden />
          <input
            type="search"
            placeholder="Search courses…"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search courses"
          />
        </div>
      )}

      {isMobile && (
        <header className="sp-mobile-bar">
          <button
            type="button"
            className="sp-icon-btn"
            onClick={onToggleSidebar}
            aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
          >
            {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <div className="sp-mobile-bar__center">
            <span className="sp-mobile-bar__eyebrow">QoreAcademy</span>
            <span className="sp-mobile-bar__title">{activeLabel}</span>
          </div>
          <div className="sp-avatar sp-avatar--sm">{studentInfo.avatar}</div>
        </header>
      )}

      {isMobile && (
        <div
          className={`sp-overlay ${sidebarOpen ? 'sp-overlay--visible' : ''}`}
          onClick={onToggleSidebar}
          aria-hidden
        />
      )}

      <aside
        className={[
          'sp-sidebar',
          sidebarCollapsed && !isMobile ? 'sp-sidebar--collapsed' : '',
          isMobile ? (sidebarOpen ? 'sp-sidebar--open' : 'sp-sidebar--hidden') : '',
        ].filter(Boolean).join(' ')}
      >
        {!isMobile && (
          <div className="sp-sidebar__top">
            <Link to="/" className="sp-brand">
              <span className="sp-brand__mark">Q</span>
              {!sidebarCollapsed && (
                <span className="sp-brand__text">
                  <strong>QoreAcademy</strong>
                  <small>Student Portal</small>
                </span>
              )}
            </Link>
            <button
              type="button"
              className="sp-icon-btn sp-icon-btn--ghost"
              onClick={onToggleCollapse}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          </div>
        )}

        <div className={`sp-profile ${sidebarCollapsed && !isMobile ? 'sp-profile--collapsed' : ''}`}>
          <div className="sp-profile__ring">
            <svg viewBox="0 0 56 56" className="sp-profile__svg" aria-hidden>
              <circle cx="28" cy="28" r="24" fill="none" stroke="rgba(139,92,246,0.15)" strokeWidth="3" />
              <circle
                cx="28"
                cy="28"
                r="24"
                fill="none"
                stroke="url(#spProgressGrad)"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={`${(stats.averageProgress / 100) * 151} 151`}
                transform="rotate(-90 28 28)"
              />
              <defs>
                <linearGradient id="spProgressGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#8b5cf6" />
                  <stop offset="100%" stopColor="#22d3ee" />
                </linearGradient>
              </defs>
            </svg>
            <div className="sp-avatar">{studentInfo.avatar}</div>
          </div>
          {(!sidebarCollapsed || isMobile) && (
            <div className="sp-profile__info">
              <div className="sp-profile__name">{studentInfo.name}</div>
              <div className="sp-profile__program">{studentInfo.program}</div>
              <div className="sp-profile__progress">
                <div className="sp-profile__bar">
                  <div className="sp-profile__fill" style={{ width: `${stats.averageProgress}%` }} />
                </div>
                <span>{stats.averageProgress}% avg</span>
              </div>
            </div>
          )}
        </div>

        <nav className="sp-nav" aria-label="Portal navigation">
          {navGroups.map((group) => (
            <div key={group.label} className="sp-nav__group">
              {(!sidebarCollapsed || isMobile) && (
                <div className="sp-nav__label">{group.label}</div>
              )}
              {group.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={[
                    'sp-nav__item',
                    activeTab === item.id ? 'sp-nav__item--active' : '',
                    sidebarCollapsed && !isMobile ? 'sp-nav__item--collapsed' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => onTabChange(item.id)}
                  title={item.label}
                >
                  <span className="sp-nav__icon">{item.icon}</span>
                  {(!sidebarCollapsed || isMobile) && (
                    <>
                      <span className="sp-nav__text">{item.label}</span>
                      {item.badge && <span className="sp-nav__pill sp-nav__pill--ai">{item.badge}</span>}
                      {item.count > 0 && !item.badge && (
                        <span className="sp-nav__pill">{item.count}</span>
                      )}
                    </>
                  )}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className={`sp-sidebar__footer ${sidebarCollapsed && !isMobile ? 'sp-sidebar__footer--collapsed' : ''}`}>
          <Link to="/aitutoring" className="sp-ai-link">
            <Sparkles size={16} />
            {(!sidebarCollapsed || isMobile) && <span>Ask Qore AI</span>}
          </Link>
          <button
            type="button"
            className={`sp-footer-btn ${activeTab === 'settings' ? 'sp-footer-btn--active' : ''}`}
            onClick={onOpenSettings}
          >
            <Settings size={16} />
            {(!sidebarCollapsed || isMobile) && <span>Settings</span>}
          </button>
          <button type="button" className="sp-footer-btn sp-footer-btn--danger" onClick={onLogout}>
            <LogOut size={16} />
            {(!sidebarCollapsed || isMobile) && <span>Logout</span>}
          </button>
        </div>
      </aside>

      <div
        className={[
          'sp-main-wrap',
          sidebarCollapsed && !isMobile ? 'sp-main-wrap--collapsed' : '',
          isMobile ? 'sp-main-wrap--mobile' : '',
        ].filter(Boolean).join(' ')}
      >
        {!isMobile && (
          <header className="sp-topbar">
            <div className="sp-topbar__left">
              <div className="sp-breadcrumb">
                <span>Portal</span>
                <ChevronRight size={14} aria-hidden />
                <strong>{activeLabel}</strong>
              </div>
              {activeTabMeta?.subtitle && (
                <p className="sp-topbar__subtitle">
                  {activeTabMeta.subtitle}
                  <span className="sp-kbd-hint"> · Press 1–5 for quick nav</span>
                </p>
              )}
            </div>

            <div className="sp-topbar__search">
              <Search size={16} className="sp-topbar__search-icon" aria-hidden />
              <input
                type="search"
                placeholder="Search courses, labs, resources…"
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                aria-label="Search portal"
              />
            </div>

            <div className="sp-topbar__actions">
              <button type="button" className="sp-icon-btn sp-icon-btn--ghost sp-icon-btn--notif" aria-label="Notifications">
                <Bell size={18} />
                <span className="sp-notif-dot" aria-hidden />
              </button>
              <Link to="/aitutoring" className="sp-topbar__ai">
                <Sparkles size={16} />
                Qore AI
              </Link>
              <div className="sp-avatar sp-avatar--sm">{studentInfo.avatar}</div>
            </div>
          </header>
        )}

        {!isMobile && quickTabs.length > 0 && (
          <nav className="sp-tab-rail" aria-label="Quick sections">
            {quickTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`sp-tab-rail__btn ${activeTab === tab.id ? 'sp-tab-rail__btn--active' : ''}`}
                onClick={() => onTabChange(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        )}

        {error && (
          <div className="sp-error-banner">
            <span>{error}</span>
            <button type="button" className="sp-btn sp-btn--primary sp-btn--sm" onClick={onRefresh}>
              Retry
            </button>
          </div>
        )}

        <main className="sp-main">{children}</main>
      </div>

      {showContinueFab && (
        <button type="button" className="sp-fab" onClick={onContinueFocus} aria-label="Continue learning">
          <Play size={18} fill="currentColor" />
          Resume
        </button>
      )}

      {isMobile && (
        <nav className="sp-bottom-nav" aria-label="Quick navigation">
          {MOBILE_NAV.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                className={`sp-bottom-nav__item ${activeTab === item.id ? 'sp-bottom-nav__item--active' : ''}`}
                onClick={() => onTabChange(item.id)}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </button>
            );
          })}
          <button
            type="button"
            className={`sp-bottom-nav__item ${!MOBILE_NAV.some((n) => n.id === activeTab) ? 'sp-bottom-nav__item--active' : ''}`}
            onClick={onToggleSidebar}
          >
            <MoreHorizontal size={20} />
            <span>More</span>
          </button>
        </nav>
      )}
    </div>
  );
};

export default StudentPortalShell;